import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CardComponent } from '@shared/components/card/card.component';
import { CollapsibleSectionComponent } from '@shared/components/collapsible-section/collapsible-section.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { Utils } from '@shared/utils/utils';

import { injectInvestigatorForecastMetadata } from '../../api/investigator-metadata.api';

type Step = {
  lastUpdated?: string;
  linkTitle: string;
  onClick: VoidFunction;
  title: string;
};

@Component({
  imports: [CollapsibleSectionComponent, ButtonComponent, CardComponent, DatePipe],
  selector: 'aux-reforecast-steps',
  template: `
    <collapsible-section
      label="Required Setup and Reforecast Steps"
      subText="Complete or review these steps to enable or update dynamic forecasting."
    >
      <div class="grid grid-cols-3 gap-4">
        @for (step of steps(); track step.title) {
          <aux-card [bottomTemplate]="bottomTemplate" className="h-full" />

          <ng-template #bottomTemplate>
            <div class="flex flex-col gap-2">
              <p>{{ step.title }}</p>
              <p class="text-sm">
                Last Updated:
                {{ (step.lastUpdated | date: 'MMMM d, y, h:mm a') || zeroHyphen }}
              </p>
              <aux-button
                variant="secondary"
                [label]="step.linkTitle"
                icon="ExternalLink"
                (click)="step.onClick()"
              />
            </div>
          </ng-template>
        }
      </div>
    </collapsible-section>
  `,
})
export class ReforecastStepsComponent {
  private router = inject(Router);

  metadata = injectInvestigatorForecastMetadata();

  steps = computed(() => {
    const {
      patient_curve_latest_update_date,
      protocol_latest_update_date,
      site_curve_latest_update_date,
    } = this.metadata.data() || {};

    return <Step[]>[
      {
        lastUpdated: protocol_latest_update_date,
        linkTitle: 'Go to Protocol',
        onClick: () => {
          void this.router.navigate([
            `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX}`,
          ]);
        },
        title: 'Configure your Protocol settings & Discontinuation rate',
      },
      {
        lastUpdated: patient_curve_latest_update_date,
        linkTitle: 'Go to Patient Curves',
        onClick: () => {
          void this.router.navigate([
            ROUTING_PATH.FORECAST_ROUTING.INDEX,
            ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX,
            ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX,
            ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES,
          ]);
        },
        title: 'Set up your Patient Curves',
      },
      {
        lastUpdated: site_curve_latest_update_date,
        linkTitle: 'Go to Site Curves',
        onClick: () => {
          void this.router.navigate([
            ROUTING_PATH.FORECAST_ROUTING.INDEX,
            ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX,
            ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.INDEX,
            ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.CURVES,
          ]);
        },
        title: 'Set up your Site Curves',
      },
    ];
  });

  zeroHyphen = Utils.zeroHyphen;
}
